import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Grid,
  Typography,
  CircularProgress,
  Alert,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  TextField,
  Paper,
  Tabs,
  Tab,
  Divider,
  Stack,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { 
  Play, 
  Pause, 
  RefreshCw,
  Zap,
  Clock,
  Settings,
  Info,
  Plus,
  Trash2,
  Search,
  Activity,
  ChevronRight,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';
import { 
  GlassContainer, 
  GlassmorphicCard, 
  PageTitle,
  SectionTitle,
  BodyText,
  SecondaryText,
  PrimaryButton,
  SecondaryButton,
  getStatusChipStyles,
  StatusChipBase,
  IconWrapper,
  fadeInUp
} from '../../styles/sharedStyles';

const HAAutomations = ({ apiBaseUrl, token }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [savedAutomations, setSavedAutomations] = useState([]);
  const [availableAutomations, setAvailableAutomations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [controlLoading, setControlLoading] = useState(null);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedAutomations, setSelectedAutomations] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedAutomationDetails, setSelectedAutomationDetails] = useState(null);
  const [automationLog, setAutomationLog] = useState([]);
  const [automationConfig, setAutomationConfig] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsTab, setDetailsTab] = useState(0);

  // Filter available automations based on search query
  const filteredAvailableAutomations = availableAutomations.filter(automation => {
    const query = searchQuery.toLowerCase();
    return (
      automation.alias.toLowerCase().includes(query) ||
      (automation.description && automation.description.toLowerCase().includes(query))
    );
  });

  const fetchSavedAutomations = useCallback(async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/api/homeassistant/automations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedAutomations(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch saved automations:', err);
      setError('Failed to fetch saved automations');
    } finally {
      setLoading(false);
    }
  }, [apiBaseUrl, token]);

  const fetchAvailableAutomations = useCallback(async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/api/homeassistant/automations/available`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAvailableAutomations(response.data);
    } catch (err) {
      console.error('Failed to fetch available automations:', err);
    }
  }, [apiBaseUrl, token]);

  useEffect(() => {
    fetchSavedAutomations();
    const interval = setInterval(fetchSavedAutomations, 30000); // Refresh every 30s
    return () => clearInterval(interval);
  }, [fetchSavedAutomations]);

  const handleOpenAddDialog = async () => {
    setSelectedAutomations([]);
    await fetchAvailableAutomations();
    setAddDialogOpen(true);
  };

  const handleCloseAddDialog = () => {
    setAddDialogOpen(false);
  };

  const handleToggleSelectAutomation = (automation) => {
    setSelectedAutomations(prev => {
      if (prev.some(item => item.id === automation.id)) {
        return prev.filter(item => item.id !== automation.id);
      }
      return [...prev, automation];
    });
  };

  const handleAddSelectedAutomations = async () => {
    try {
      for (const automation of selectedAutomations) {
        await axios.post(`${apiBaseUrl}/api/homeassistant/automations`, automation, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      await fetchSavedAutomations();
      handleCloseAddDialog();
    } catch (err) {
      console.error('Failed to add automations:', err);
      setError('Failed to add automations');
    }
  };

  const handleToggleAutomation = async (automationId, currentState) => {
    try {
      setControlLoading(automationId);
      const newState = !currentState;
      
      await axios.post(`${apiBaseUrl}/api/homeassistant/control`, {
        entity_id: `automation.${automationId}`,
        action: newState ? 'turn_on' : 'turn_off'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSavedAutomations(prev => prev.map(automation => {
        if (automation.automation_id === automationId) {
          return { ...automation, enabled: newState };
        }
        return automation;
      }));
    } catch (err) {
      console.error('Failed to toggle automation:', err);
      setError('Failed to toggle automation');
    } finally {
      setControlLoading(null);
    }
  };

  const handleExecuteAutomation = async (automationId) => {
    try {
      setControlLoading(automationId);
      
      await axios.post(`${apiBaseUrl}/api/homeassistant/control`, {
        entity_id: `automation.${automationId}`,
        action: 'trigger'
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setError(null);
    } catch (err) {
      console.error('Failed to execute automation:', err);
      setError('Failed to execute automation');
    } finally {
      setControlLoading(null);
    }
  };

  const handleDeleteAutomation = async (automationId) => {
    try {
      setControlLoading(automationId);
      
      await axios.delete(`${apiBaseUrl}/api/homeassistant/automations/${automationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSavedAutomations(prev => prev.filter(automation => automation.id !== automationId));
      setError(null);
    } catch (err) {
      console.error('Failed to delete automation:', err);
      setError('Failed to delete automation');
    } finally {
      setControlLoading(null);
    }
  };

  const handleOpenDetailsDialog = async (automation) => {
    setSelectedAutomationDetails(automation);
    setDetailsDialogOpen(true);
    setDetailsLoading(true);
    setAutomationLog([]);
    setAutomationConfig(null);
    setDetailsTab(0);

    try {
      // Fetch logs and config in parallel
      const [logRes, configRes] = await Promise.all([
        axios.get(`${apiBaseUrl}/api/homeassistant/automations/${automation.entity_id}/log`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        automation.unique_id ? axios.get(`${apiBaseUrl}/api/homeassistant/automations/${automation.unique_id}/config`, {
          headers: { Authorization: `Bearer ${token}` }
        }) : Promise.resolve({ data: { error: 'No unique ID' } })
      ]);

      setAutomationLog(logRes.data);
      if (!configRes.data.error) {
        setAutomationConfig(configRes.data);
      }
    } catch (err) {
      console.error('Failed to fetch automation details:', err);
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
  };

  const formatTriggers = (triggers) => {
    if (!triggers || !Array.isArray(triggers) || triggers.length === 0) return null;

    const timeTriggers = triggers.filter(t => t.trigger === 'time');
    if (timeTriggers.length === 0) return null;

    return timeTriggers.map((t, idx) => {
      let label = '';
      const time = t.at.length > 5 ? t.at.substring(0, 5) : t.at; // HH:mm

      if (t.id === 'weekday') {
        label = `MON-FRI ${time}`;
      } else if (t.id === 'weekend') {
        label = `SAT-SUN ${time}`;
      } else {
        label = time;
      }

      return (
        <Chip 
          key={idx}
          size="small"
          label={label}
          icon={<Clock size={12} />}
          sx={{ 
            height: '20px',
            fontSize: '0.65rem',
            background: 'rgba(255, 255, 255, 0.05)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: 'rgba(255, 255, 255, 0.7)',
            '& .MuiChip-icon': { color: 'inherit' },
            mr: 0.5,
            mb: 0.5
          }}
        />
      );
    });
  };

  if (loading) {
    return (
      <GlassContainer sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '400px',
        animation: `${fadeInUp} 0.6s ease-out`
      }}>
        <CircularProgress sx={{ color: '#00d4ff' }} />
      </GlassContainer>
    );
  }

  return (
    <GlassContainer sx={{ 
      p: { xs: 2, sm: 3, md: 4 },
      animation: `${fadeInUp} 0.6s ease-out`
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <SectionTitle sx={{ fontSize: isMobile ? '0.9rem' : '1.1rem' }}>
          Home Assistant Automations
        </SectionTitle>
        <PrimaryButton
          startIcon={<Plus size={18} />}
          onClick={handleOpenAddDialog}
        >
          Add Automation
        </PrimaryButton>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ 
          background: 'rgba(255, 68, 68, 0.1)',
          border: '1px solid rgba(255, 68, 68, 0.3)',
          color: '#ff4444',
          '& .MuiAlert-icon': {
            color: '#ff4444'
          },
          mb: 3
        }}>
          {error}
        </Alert>
      )}

      {savedAutomations.length === 0 ? (
        <Box sx={{ 
          textAlign: 'center',
          py: 8,
          color: 'rgba(255, 255, 255, 0.6)'
        }}>
          <Zap size={64} sx={{ mb: 2, opacity: 0.3 }} />
          <Typography variant="h6" sx={{ mb: 1 }}>
            No automations added
          </Typography>
          <BodyText sx={{ mb: 3 }}>
            Click "Add Automation" to add your first automation
          </BodyText>
          <PrimaryButton
            startIcon={<Plus size={18} />}
            onClick={handleOpenAddDialog}
          >
            Add Automation
          </PrimaryButton>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {savedAutomations.map((automation) => (
            <Grid item xs={12} sm={6} md={4} key={automation.id}>
              <GlassmorphicCard sx={{ 
                p: 3,
                position: 'relative',
                overflow: 'visible'
              }}>
                <Box sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  mb: 2
                }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" sx={{ 
                      color: '#ffffff',
                      fontWeight: 600,
                      mb: 0.5,
                      fontSize: '1.1rem'
                    }}>
                      {automation.name}
                    </Typography>
                    <SecondaryText sx={{ 
                      fontSize: '0.85rem',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {automation.description || 'No description'}
                    </SecondaryText>
                    {automation.last_triggered && (
                      <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Clock size={14} sx={{ color: 'rgba(255, 255, 255, 0.5)' }} />
                        <SecondaryText sx={{ fontSize: '0.75rem' }}>
                          Last run: {new Date(automation.last_triggered).toLocaleString()}
                        </SecondaryText>
                      </Box>
                    )}
                    {automation.triggers && automation.triggers.length > 0 && (
                      <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap' }}>
                        {formatTriggers(automation.triggers)}
                      </Box>
                    )}
                  </Box>
                  
                  <IconWrapper sx={{ 
                    ml: 2,
                    background: automation.enabled 
                      ? 'rgba(0, 255, 136, 0.1)'
                      : 'rgba(255, 68, 68, 0.1)',
                    border: `1px solid ${automation.enabled 
                      ? 'rgba(0, 255, 136, 0.3)'
                      : 'rgba(255, 68, 68, 0.3)'}`
                  }}>
                    <Zap 
                      size={24} 
                      color={automation.enabled ? '#00ff88' : '#ff4444'}
                    />
                  </IconWrapper>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <StatusChipBase
                    label={automation.enabled ? 'Enabled' : 'Disabled'}
                    sx={getStatusChipStyles(automation.enabled ? 'running' : 'stopped')}
                  />
                </Box>

                <Box sx={{ 
                  display: 'flex',
                  gap: 1,
                  justifyContent: 'flex-end',
                  mt: 'auto'
                }}>
                  <SecondaryButton
                    size="small"
                    startIcon={automation.enabled ? <Pause size={16} /> : <Play size={16} />}
                    onClick={() => handleToggleAutomation(automation.automation_id, automation.enabled)}
                    disabled={controlLoading === automation.id}
                    sx={{
                      borderColor: automation.enabled ? '#ff4444' : '#00ff88',
                      color: automation.enabled ? '#ff4444' : '#00ff88',
                      '&:hover': {
                        background: automation.enabled 
                          ? 'rgba(255, 68, 68, 0.1)'
                          : 'rgba(0, 255, 136, 0.1)',
                        borderColor: automation.enabled ? '#ff4444' : '#00ff88'
                      }
                    }}
                  >
                    {automation.enabled ? 'Disable' : 'Enable'}
                  </SecondaryButton>

                  <PrimaryButton
                    size="small"
                    startIcon={<Play size={16} />}
                    onClick={() => handleExecuteAutomation(automation.automation_id)}
                    disabled={controlLoading === automation.id || !automation.enabled}
                  >
                    Execute
                  </PrimaryButton>

                  <IconButton
                    size="small"
                    onClick={() => handleOpenDetailsDialog(automation)}
                    sx={{
                      color: '#00d4ff',
                      '&:hover': {
                        background: 'rgba(0, 212, 255, 0.1)'
                      }
                    }}
                  >
                    <Info size={16} />
                  </IconButton>

                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteAutomation(automation.id)}
                    disabled={controlLoading === automation.id}
                    sx={{
                      color: '#ff4444',
                      '&:hover': {
                        background: 'rgba(255, 68, 68, 0.1)'
                      }
                    }}
                  >
                    <Trash2 size={16} />
                  </IconButton>
                </Box>
              </GlassmorphicCard>
            </Grid>
          ))}
        </Grid>
      )}

       {/* Add Automation Dialog */}
      <Dialog
        open={addDialogOpen}
        onClose={handleCloseAddDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(10, 14, 39, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '24px'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#ffffff',
          fontSize: '1.5rem',
          fontWeight: 600,
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <Search size={24} sx={{ color: '#00d4ff' }} />
          Add Automation
        </DialogTitle>
        
        <DialogContent sx={{ mt: 2 }}>
          <Typography variant="body1" sx={{ 
            color: 'rgba(255, 255, 255, 0.8)',
            mb: 3
          }}>
            Select automations from your Home Assistant to add to your dashboard.
          </Typography>

          {/* Search Input */}
          <TextField
            placeholder="Search automations by name or description..."
            fullWidth
            size="small"
            sx={{
              mb: 2,
              '& .MuiOutlinedInput-root': {
                color: 'white',
                '& fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.1)'
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(255, 255, 255, 0.2)'
                },
                '&.Mui-focused fieldset': {
                  borderColor: '#00d4ff'
                }
              },
              '& .MuiInputLabel-root': {
                color: 'rgba(255, 255, 255, 0.6)'
              },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(255, 255, 255, 0.4)'
              }
            }}
            onChange={(e) => setSearchQuery(e.target.value)}
          />

          <Paper sx={{ 
            background: 'rgba(255, 255, 255, 0.02)',
            border: '1px solid rgba(255, 255, 255, 0.08)',
            borderRadius: '16px',
            maxHeight: '400px',
            overflow: 'auto'
          }}>
            <List>
              {filteredAvailableAutomations.map((automation) => (
                <ListItem
                  key={automation.id}
                  onClick={() => handleToggleSelectAutomation(automation)}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.03)'
                    }
                  }}
                >
                  <ListItemIcon>
                    <Checkbox
                      checked={selectedAutomations.some(item => item.id === automation.id)}
                      sx={{
                        color: '#00d4ff',
                        '&.Mui-checked': {
                          color: '#00ff88'
                        }
                      }}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={automation.alias}
                    secondary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                        <SecondaryText sx={{ fontSize: '0.75rem' }}>
                          {automation.description || 'No description'}
                        </SecondaryText>
                        {automation.last_triggered && (
                          <Chip
                            label={`Last run: ${new Date(automation.last_triggered).toLocaleString()}`}
                            size="small"
                            sx={{
                              background: 'rgba(0, 212, 255, 0.1)',
                              border: '1px solid rgba(0, 212, 255, 0.2)',
                              color: '#00d4ff',
                              fontSize: '0.7rem'
                            }}
                          />
                        )}
                      </Box>
                    }
                    sx={{
                      '& .MuiListItemText-primary': {
                        color: '#ffffff',
                        fontSize: '0.95rem',
                        fontWeight: 500
                      },
                      '& .MuiListItemText-secondary': {
                        color: 'rgba(255, 255, 255, 0.6)'
                      }
                    }}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </DialogContent>

        <DialogActions sx={{ 
          p: 3,
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Button
            onClick={handleCloseAddDialog}
            sx={{
              color: 'rgba(255, 255, 255, 0.6)',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.05)',
                color: '#ffffff'
              }
            }}
          >
            Cancel
          </Button>

          <PrimaryButton
            onClick={handleAddSelectedAutomations}
            disabled={selectedAutomations.length === 0}
          >
            Add Selected ({selectedAutomations.length})
          </PrimaryButton>
        </DialogActions>
      </Dialog>

      {/* Automation Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            background: 'rgba(10, 14, 39, 0.95)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: '24px',
            minHeight: '500px'
          }
        }}
      >
        <DialogTitle sx={{ 
          color: '#ffffff',
          fontSize: '1.5rem',
          fontWeight: 600,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Activity size={24} sx={{ color: '#00d4ff' }} />
            <Box>
              <Typography variant="h6" sx={{ color: '#fff', lineHeight: 1.2 }}>
                {selectedAutomationDetails?.name}
              </Typography>
              <SecondaryText sx={{ fontSize: '0.75rem', fontWeight: 400 }}>
                {selectedAutomationDetails?.entity_id}
              </SecondaryText>
            </Box>
          </Box>
          <IconButton onClick={handleCloseDetailsDialog} sx={{ color: 'rgba(255,255,255,0.5)' }}>
            <Trash2 size={20} />
          </IconButton>
        </DialogTitle>
        
        <DialogContent sx={{ p: 0 }}>
          <Tabs 
            value={detailsTab} 
            onChange={(e, v) => setDetailsTab(v)}
            sx={{
              px: 3,
              borderBottom: '1px solid rgba(255, 255, 255, 0.05)',
              '& .MuiTabs-indicator': {
                backgroundColor: '#00d4ff',
              },
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.5)',
                '&.Mui-selected': {
                  color: '#00d4ff',
                }
              }
            }}
          >
            <Tab label="Overview" />
            <Tab label="Logbook" />
            <Tab label="Configuration" />
          </Tabs>

          <Box sx={{ p: 3, maxHeight: '60vh', overflow: 'auto' }}>
            {detailsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress size={32} sx={{ color: '#00d4ff' }} />
              </Box>
            ) : (
              <>
                {/* Tab 0: Overview */}
                {detailsTab === 0 && (
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.4)', mb: 1, display: 'block' }}>
                        Description
                      </Typography>
                      <Typography sx={{ color: '#fff', mb: 3 }}>
                        {selectedAutomationDetails?.description || 'No description provided.'}
                      </Typography>

                      <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.4)', mb: 1, display: 'block' }}>
                        Stats
                      </Typography>
                      <Stack spacing={1.5}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <SecondaryText>Mode</SecondaryText>
                          <Typography sx={{ color: '#fff' }}>{selectedAutomationDetails?.mode || 'single'}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <SecondaryText>State</SecondaryText>
                          <StatusChipBase
                            label={selectedAutomationDetails?.enabled ? 'Enabled' : 'Disabled'}
                            size="small"
                            sx={getStatusChipStyles(selectedAutomationDetails?.enabled ? 'running' : 'stopped')}
                          />
                        </Box>
                        {selectedAutomationDetails?.current !== undefined && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <SecondaryText>Currently Running</SecondaryText>
                            <Typography sx={{ color: '#00ff88', fontWeight: 600 }}>
                              {selectedAutomationDetails.current} / {selectedAutomationDetails.max_runs || 1}
                            </Typography>
                          </Box>
                        )}
                      </Stack>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="overline" sx={{ color: 'rgba(255,255,255,0.4)', mb: 1, display: 'block' }}>
                        Recent Activity
                      </Typography>
                      {automationLog.length > 0 ? (
                        <List dense disablePadding>
                          {automationLog.slice(0, 5).map((log, idx) => (
                            <ListItem key={idx} sx={{ px: 0, py: 1 }}>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                <Clock size={14} color="rgba(255,255,255,0.3)" />
                              </ListItemIcon>
                              <ListItemText 
                                primary={log.message || log.name}
                                secondary={new Date(log.when).toLocaleString()}
                                primaryTypographyProps={{ sx: { color: '#fff', fontSize: '0.85rem' } }}
                                secondaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' } }}
                              />
                            </ListItem>
                          ))}
                        </List>
                      ) : (
                        <Typography sx={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.85rem' }}>
                          No recent activity found.
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                )}

                {/* Tab 1: Logbook */}
                {detailsTab === 1 && (
                  <Box>
                    {automationLog.length > 0 ? (
                      <List dense>
                        {automationLog.map((log, idx) => (
                          <ListItem key={idx} divider={idx !== automationLog.length - 1} sx={{ borderColor: 'rgba(255,255,255,0.05)' }}>
                            <ListItemIcon>
                              <Activity size={18} color="#00d4ff" />
                            </ListItemIcon>
                            <ListItemText 
                              primary={log.message || log.name}
                              secondary={
                                <Box sx={{ display: 'flex', gap: 2, mt: 0.5 }}>
                                  <span>{new Date(log.when).toLocaleString()}</span>
                                  {log.source && (
                                    <span style={{ color: 'rgba(0, 212, 255, 0.6)' }}>
                                      Source: {log.source}
                                    </span>
                                  )}
                                </Box>
                              }
                              primaryTypographyProps={{ sx: { color: '#fff', fontSize: '0.9rem', fontWeight: 500 } }}
                              secondaryTypographyProps={{ sx: { color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' } }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <AlertCircle size={48} color="rgba(255,255,255,0.1)" style={{ marginBottom: 16 }} />
                        <Typography sx={{ color: 'rgba(255,255,255,0.4)' }}>
                          No logbook entries for the last 24 hours.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}

                {/* Tab 2: Configuration */}
                {detailsTab === 2 && (
                  <Box>
                    {automationConfig ? (
                      <Stack spacing={3}>
                        <Box>
                          <Typography variant="overline" sx={{ color: '#00d4ff', fontWeight: 600, mb: 1, display: 'block' }}>
                            Triggers
                          </Typography>
                          <Paper sx={{ p: 2, background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.05)' }}>
                            <pre style={{ margin: 0, color: '#fff', fontSize: '0.85rem', overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
                              {JSON.stringify(automationConfig.trigger || automationConfig.triggers, null, 2)}
                            </pre>
                          </Paper>
                        </Box>
                        <Box>
                          <Typography variant="overline" sx={{ color: '#00ff88', fontWeight: 600, mb: 1, display: 'block' }}>
                            Actions
                          </Typography>
                          <Paper sx={{ p: 2, background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.05)' }}>
                            <pre style={{ margin: 0, color: '#fff', fontSize: '0.85rem', overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
                              {JSON.stringify(automationConfig.action || automationConfig.actions, null, 2)}
                            </pre>
                          </Paper>
                        </Box>
                        {automationConfig.condition && (
                          <Box>
                            <Typography variant="overline" sx={{ color: '#ffcc00', fontWeight: 600, mb: 1, display: 'block' }}>
                              Conditions
                            </Typography>
                            <Paper sx={{ p: 2, background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.05)' }}>
                              <pre style={{ margin: 0, color: '#fff', fontSize: '0.85rem', overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
                                {JSON.stringify(automationConfig.condition, null, 2)}
                              </pre>
                            </Paper>
                          </Box>
                        )}
                      </Stack>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <AlertCircle size={48} color="rgba(255,255,255,0.1)" style={{ marginBottom: 16 }} />
                        <Typography sx={{ color: 'rgba(255,255,255,0.4)', mb: 2 }}>
                          Configuration not available.
                        </Typography>
                        <SecondaryText sx={{ fontSize: '0.8rem' }}>
                          Note: Only automations created via the Home Assistant UI are accessible through this API.
                        </SecondaryText>
                      </Box>
                    )}
                  </Box>
                )}
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={handleCloseDetailsDialog} sx={{ color: 'rgba(255,255,255,0.6)' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </GlassContainer>
  );
};

export default HAAutomations;
